<template>
  <div>
    <!-- AI 输入面板 -->
    <div v-if="diagnosisStatus === 'idle'" class="diagnosis-panel">
      <div class="panel-header">
        <span class="pdot cyan"></span>AI智能诊断输入
        <span class="pid">品牌渠道自动调研</span>
      </div>
      <div class="panel-body">
        <div class="ai-hero">
          <h2>输入品牌名，<span class="glow">AI自动完成诊断</span></h2>
          <p>系统将自动搜索品牌在天猫、京东、抖音、小红书等平台的渠道覆盖情况，分析DTC布局和分销体系，生成18项指标评分与优化建议。</p>
          <div class="ai-form">
            <div class="ai-field">
              <label>品牌名称</label>
              <input
                type="text"
                class="ai-input"
                v-model="brandName"
                placeholder="例如：蒙牛、海尔、花西子..."
                @keyup.enter="startDiagnosis"
              />
            </div>
            <div class="ai-field">
              <label>行业 / 品类</label>
              <select class="ai-select" v-model="industry">
                <option v-for="ind in industries" :key="ind" :value="ind">{{ ind }}</option>
              </select>
            </div>
            <button
              class="ai-btn"
              :disabled="!brandName.trim()"
              @click="startDiagnosis"
            >
              开始AI诊断
            </button>
            <div class="ai-hint">基于真实网络搜索数据 · 约15-30秒完成</div>
          </div>
        </div>
      </div>
    </div>

    <!-- AI 加载动画 -->
    <div v-if="diagnosisStatus === 'loading'" class="diagnosis-panel">
      <div class="panel-header">
        <span class="pdot cyan"></span>AI调研中
        <span class="pid">正在分析渠道数据</span>
      </div>
      <div class="panel-body">
        <div class="ai-loading">
          <div class="radar">
            <div class="radar-sweep"></div>
            <div class="radar-dot"></div>
          </div>
          <div class="load-title">{{ currentStepTitle }}</div>
          <div class="load-steps">
            <div
              v-for="(step, idx) in loadingSteps"
              :key="idx"
              class="load-step"
              :class="{ active: currentStep === idx, done: currentStep > idx }"
            >
              <span class="ls-icon">{{ currentStep > idx ? '✓' : '○' }}</span>
              <span class="ls-text">{{ step.label }}</span>
              <span class="ls-bar"></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import { INDUSTRIES, DEFAULT_SCORES } from '../../utils/constants'

// 前端静态数据 — 对接后端后可移除
import { BRM_DB, BRAND_DB } from '../../utils/mockData'

export default {
  name: 'AiDiagnosisTab',
  data() {
    return {
      brandName: '蒙牛',
      industry: '食品饮料',
      currentStep: -1,
      loadingSteps: [
        { label: '搜索主流电商平台覆盖' },
        { label: '检测内容/社交电商布局' },
        { label: '分析DTC自有渠道' },
        { label: '评估分销体系' },
        { label: '分析业态多样性' },
        { label: '生成诊断结果' }
      ]
    }
  },
  computed: {
    ...mapState('diagnosis', ['diagnosisStatus']),
    industries() { return INDUSTRIES },
    currentStepTitle() {
      const titles = [
        `正在搜索 ${this.brandName} 在 BRM 系统中的渠道数据...`,
        `正在搜索 ${this.brandName} 在天猫/京东/拼多多的覆盖...`,
        `正在检测 ${this.brandName} 在抖音/小红书/快手的布局...`,
        `正在分析 ${this.brandName} 的业态多样性和DTC渠道...`,
        `正在评估 ${this.brandName} 的分销体系和渠道健康度...`,
        `正在生成 ${this.brandName} 的诊断结果...`
      ]
      return this.currentStep >= 0 && this.currentStep < titles.length
        ? titles[this.currentStep]
        : '正在初始化诊断引擎...'
    }
  },
  methods: {
    ...mapMutations('diagnosis', [
      'SET_AI_MODE', 'SET_AI_BRAND', 'SET_AI_INDUSTRY',
      'SET_DIAGNOSIS_STATUS', 'SET_BATCH_SCORES', 'SET_BRM_DATA', 'SET_AI_DATA'
    ]),
    ...mapActions('diagnosis', ['setScore']),

    async startDiagnosis() {
      const brand = this.brandName.trim()
      if (!brand) return

      this.SET_AI_MODE(true)
      this.SET_AI_BRAND(brand)
      this.SET_AI_INDUSTRY(this.industry)
      this.SET_DIAGNOSIS_STATUS('loading')

      // 模拟AI搜索步骤
      this.currentStep = -1
      for (let i = 0; i < 6; i++) {
        this.currentStep = i
        await this._delay(800 + Math.random() * 600)
      }

      // ─── 第一步：查 BRM 数据 ───
      const brmData = BRM_DB[brand] || null
      const scores = {}
      const brmSource = {}

      if (brmData && brmData.brmScores) {
        for (const qid in brmData.brmScores) {
          scores[qid] = brmData.brmScores[qid].score
          brmSource[qid] = true
        }
      }

      // ─── 第二步：查 AI 搜索数据 ───
      const aiData = BRAND_DB[brand] || null
      if (aiData && aiData.scores) {
        for (const qid in aiData.scores) {
          scores[qid] = aiData.scores[qid]
          if (brmSource[qid]) delete brmSource[qid]
        }
      } else if (!brmData) {
        // 既无BRM也无AI数据 → 默认中等分
        for (const qid in DEFAULT_SCORES) {
          scores[qid] = DEFAULT_SCORES[qid]
        }
      }

      this.SET_BATCH_SCORES({ scores, brmSource })
      this.SET_BRM_DATA(brmData)
      this.SET_AI_DATA(aiData)
      this.SET_DIAGNOSIS_STATUS('completed')

      // 通知父组件
      this.$emit('diagnosis-complete')
    },

    _delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    }
  }
}
</script>
